import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Box, Card, Typography } from "@mui/material";
import { DataGrid, GridRowParams, PaginationModel } from "@mui/x-data-grid";
import { Claim_ClaimsDbModel } from "@safelease/service-utilities";
import ClaimDetailsDrawer from "./ClaimDetailsDrawer";
import RouterHelper from "../../utilities/router-helper";
import { getClaimsTableColumnDefs } from "./ClaimsTableColumnDefs";
import { mixpanelEventHandler } from "../../utilities/reactMixpanelHandler";
import { CLAIMS_MIXPANEL_EVENTS } from "./claimsMixpanelEvents";

interface ClaimsTableProps {
  claims: Claim_ClaimsDbModel[];
  loading: boolean;
  routerHelper: RouterHelper;
}

/**
 * Modern MUI-based claims table that works directly with REST API data
 * Uses Material-UI DataGrid for a clean, modern look
 * Supports deep linking via URL (e.g., /dashboard/claims/claim-123)
 */
function ClaimsTable({ claims, loading, routerHelper }: ClaimsTableProps) {
  const [selectedClaim, setSelectedClaim] = useState<Claim_ClaimsDbModel | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState<PaginationModel>({ page: 0, pageSize: 10 });
  const hasTrackedView = useRef(false);

  const routerParams: { claimId?: string } = useParams();
  const history = useHistory();

  // Track table viewed event on mount (only once)
  useEffect(() => {
    if (!hasTrackedView.current && !loading) {
      const locationId = routerHelper.getLocationId();
      mixpanelEventHandler(CLAIMS_MIXPANEL_EVENTS.table.viewed, {
        locationId: locationId || null,
      });
      hasTrackedView.current = true;
    }
  }, [loading, claims.length, routerHelper]);

  // Sync drawer state with URL parameters
  useEffect(() => {
    if (routerParams?.claimId && claims.length > 0) {
      const selectedClaimInQueryResult = claims.find((claim) => claim.id === routerParams.claimId);
      if (selectedClaimInQueryResult) {
        setSelectedClaim(selectedClaimInQueryResult);
        setDrawerOpen(true);
        // Note: Drawer opened tracking is handled in ClaimDetailsDrawer component
      }
    }
  }, [routerParams?.claimId, claims]);

  // Priority sorting function for statuses
  const getStatusPriority = (status: string): number => {
    const statusLower = status?.toLowerCase() || "";

    // Priority 1: Facility Action Needed (highest priority)
    if (statusLower.includes("facility action")) return 1;

    // Priority 2: Tenant Action Needed
    if (statusLower.includes("tenant action")) return 2;

    // Priority 3: Adjuster actions (Needs Review, Needs Approval, Pending Settlement)
    if (statusLower.includes("needs review")) return 3;
    if (statusLower.includes("needs approval")) return 3;
    if (statusLower.includes("pending settlement")) return 3;

    // Priority 4: New Claim
    if (statusLower.includes("new claim")) return 4;

    // Priority 5: All Closed statuses (lowest priority)
    if (statusLower.includes("closed")) return 5;
    if (statusLower === "incomplete") return 5;

    // Default priority
    return 4;
  };

  // Sort claims by status priority
  const sortedClaims = [...claims].sort((a, b) => {
    const priorityA = getStatusPriority(a.status);
    const priorityB = getStatusPriority(b.status);

    // If same priority, sort by createdAt (newest first)
    if (priorityA === priorityB) {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }

    return priorityA - priorityB;
  });

  const handleRowClick = (params: GridRowParams) => {
    const claim = params.row;
    setSelectedClaim(claim);
    setDrawerOpen(true);

    // Track row click event (adoption event)
    const locationId = routerHelper.getLocationId();
    mixpanelEventHandler(CLAIMS_MIXPANEL_EVENTS.table.rowClicked, {
      claim_id: claim.id,
      claim_status: claim.status,
      locationId: locationId || null,
    });

    // Update URL to include claim ID for deep linking
    routerHelper.navigateToRoute("claim", routerHelper.getRelationshipId(), history, claim.id);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedClaim(null);
    // Remove claim ID from URL when closing drawer
    routerHelper.navigateToRoute("dashboard", routerHelper.getRelationshipId(), history);
  };

  // Get column definitions
  const columns = getClaimsTableColumnDefs();

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, px: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: "text.primary" }}>
          Claims
        </Typography>
      </Box>

      {/* Table Card */}
      <Card
        sx={{
          height: 600,
          width: "100%",
          borderRadius: 2,
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <DataGrid
          rows={sortedClaims}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          paginationModel={paginationModel}
          onPaginationModelChange={(model) => {
            setPaginationModel(model);
            // Track pagination change
            const locationId = routerHelper.getLocationId();
            mixpanelEventHandler(CLAIMS_MIXPANEL_EVENTS.table.paginationChanged, {
              page_size: model.pageSize,
              page_number: model.page,
              locationId: locationId || null,
            });
          }}
          onRowClick={handleRowClick}
          disableRowSelectionOnClick
          sx={{
            border: "none",
            "& .MuiDataGrid-cell": {
              borderColor: "divider",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "grey.50",
              borderColor: "divider",
              fontWeight: 600,
              fontSize: "0.875rem",
            },
            "& .MuiDataGrid-row": {
              cursor: "pointer",
              transition: "background-color 0.2s ease",
              "&:hover": {
                backgroundColor: "action.hover",
              },
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-row.Mui-selected": {
              backgroundColor: "action.selected",
              "&:hover": {
                backgroundColor: "action.selected",
              },
            },
          }}
        />
      </Card>

      {/* Claim Details Drawer */}
      <ClaimDetailsDrawer
        claim={selectedClaim}
        open={drawerOpen}
        onClose={handleDrawerClose}
        drawerOpenedVia={routerParams?.claimId ? "url" : "row_click"}
      />
    </Box>
  );
}

export default ClaimsTable;
